import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from '../layout/layout';

const RoleManagement = () => {
  const [roles, setRoles] = useState([
    {
      id: 1,
      name: 'Super Admin',
      description: 'Full system access with all privileges',
      permissions: ['create', 'read', 'update', 'delete', 'manage_users', 'manage_roles', 'view_reports', 'export_data'],
      status: 'active',
      created: '2024-01-15'
    },
    {
      id: 2,
      name: 'FinOps',
      description: 'Financial operations and cost management',
      permissions: ['read', 'view_reports', 'export_data', 'manage_billing'],
      status: 'active',
      created: '2024-01-16'
    },
    {
      id: 3,
      name: 'Admin',
      description: 'Administrative access with user management',
      permissions: ['create', 'read', 'update', 'delete', 'manage_users'],
      status: 'active',
      created: '2024-01-17'
    }
  ]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [],
    status: 'active'
  });

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCreateRole = () => {
    setRoles([...roles, { ...newRole, id: roles.length + 1, created: new Date().toISOString().split('T')[0] }]);
    setDialogOpen(false);
    setNewRole({ name: '', description: '', permissions: [], status: 'active' });
  };

  const handleChange = (field, value) => {
    setNewRole(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionToggle = (permission) => {
    setNewRole(prev => {
      const permissions = prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission];
      return { ...prev, permissions };
    });
  };

  const handleEditClick = (role) => {
    
  };

  const handleDeleteClick = (roleId) => {
    setRoles(roles.filter(role => role.id !== roleId));
  };

  const columns = [
    { field: 'name', headerName: 'Role Name', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2 },
    {
      field: 'permissions',
      headerName: 'Permissions',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', maxHeight: 80, overflowY: 'auto', }}>
          {params.value.map((perm, index) => (
            <Box key={index} component="span" >
              <Typography variant="caption" sx={{ px:1, border: '1px solid', borderRadius: '4px', display: 'inline-block' }}>{perm}</Typography>
            </Box>
          ))}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false, 
      filterable: false,  
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '25px',
          textAlign: 'center',
          borderRadius: '5px',
          padding: '2px 8px',
          bgcolor: params.value === 'active' ? 'green' : 'transparent',
          color: params.value === 'active' ? 'white' : 'black'
        }}>
          {params.value}
        </Box>
      )
    },
    { field: 'created', headerName: 'Created', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false, 
      filterable: false,  
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box>
          <Button variant="text" color="primary" onClick={() => handleEditClick(params.row)}>
            <EditIcon />
          </Button>
          <Button variant="text" color="error" onClick={() => handleDeleteClick(params.row.id)}>
            <DeleteIcon />
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Layout pageType='sideBar'>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>Role Management</Typography>
        <Button variant="contained" color="primary" onClick={handleDialogOpen}>
          Add New Role
        </Button>

        <Box sx={{ width: '100%', mt: 2 }}>
          <DataGrid
            rows={roles}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            hideFooterSelectedRowCount 
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-root': {
                bgcolor: '#ffffff',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#2b61aa',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold !important',
                // textTransform: "uppercase",
              },
              '& .MuiDataGrid-cell': {
                color: '#212330',
                borderBottomWidth: '1px solid',
                borderColor: '#e0e0e0',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                bgcolor: '#fafafa',
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                bgcolor: '#ffffff',
              },
              '& .MuiDataGrid-footerContainer': {
                bgcolor: '#e0e0e0',
              },
            }}
          />
        </Box>

        <Dialog open={dialogOpen} onClose={handleDialogClose}>
           <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold'}}>Create New Role</DialogTitle>
          <DialogContent>
            <TextField
              label="Role Name"
              fullWidth
              margin="dense"
              required
              value={newRole.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <Select
              fullWidth
              margin="dense"
              value={newRole.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
            <TextField
              label="Description"
              fullWidth
              margin="dense"
              value={newRole.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>Permissions</Typography>
            {['create', 'read', 'update', 'delete', 'manage_users', 'manage_roles', 'view_reports', 'export_data'].map((perm) => (
              <FormControlLabel
                key={perm}
                control={
                  <Switch
                    checked={newRole.permissions.includes(perm)}
                    onChange={() => handlePermissionToggle(perm)}
                  />
                }
                label={perm.toUpperCase()}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">Cancel</Button>
            <Button onClick={handleCreateRole} color="primary">Create Role</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default RoleManagement;
